/**
 * Tenant-scoped user settings via /api/settings/user (PUT).
 * Prefer this over PATCH /api/user/me for prefs like onboarding_completed.
 */
import { resolveEffectiveTenant } from '@/lib/tenantUtils';

const API_BASE_URL =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';

export function getUserSettingsHeaders(userId, tenantId) {
  return {
    'Content-Type': 'application/json',
    'x-user-id': String(userId),
    'x-tenant-id': String(tenantId),
    'X-User-ID': String(userId),
    'X-Tenant-ID': String(tenantId),
  };
}

export function resolveUserSettingsContext(contextUser, contextTenant = null) {
  const userId =
    contextUser?.id || contextUser?.user_id || localStorage.getItem('user-id') || '';
  const tenantId = resolveEffectiveTenant(
    contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    contextTenant,
    contextUser,
    'instructure',
  );
  return { userId, tenantId };
}

/** PUT partial settings for the current user + tenant. */
export async function putUserSettings(userId, tenantId, patch) {
  if (!userId || !tenantId) return { ok: false, status: 0 };

  const res = await fetch(`${API_BASE_URL}/api/settings/user`, {
    method: 'PUT',
    credentials: 'include',
    headers: getUserSettingsHeaders(userId, tenantId),
    body: JSON.stringify(patch ?? {}),
  });

  return { ok: res.ok, status: res.status };
}

/** Mark onboarding complete — settings API first, then /api/user/me fallback. */
export async function persistOnboardingCompleted(contextUser, contextTenant = null) {
  const { userId, tenantId } = resolveUserSettingsContext(contextUser, contextTenant);
  if (!userId || !tenantId) return false;

  const settingsResult = await putUserSettings(userId, tenantId, {
    onboarding_completed: true,
  });
  if (settingsResult.ok) return true;

  try {
    const res = await fetch(`${API_BASE_URL}/api/user/me`, {
      method: 'PATCH',
      credentials: 'include',
      headers: getUserSettingsHeaders(userId, tenantId),
      body: JSON.stringify({
        user_id: userId,
        tenant_id: tenantId,
        onboarding_completed: true,
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * SETUP STATUS — what this rep still has to configure.
 *
 * 🔴 DERIVED SERVER-SIDE, NEVER HERE. The chatbot and the MCP connector answer
 * the same question from `services/setupStatus`; a second derivation in the FE
 * is how the onboarding screen ends up congratulating a rep on a mailbox the
 * assistant is simultaneously telling them to connect.
 *
 * `missing` is the truth. `raise` is the subset allowed to be brought up
 * unprompted — the onboarding screen may show `missing` in full because the rep
 * opened it deliberately; a banner anywhere else must use `raise`.
 */
export async function fetchSetupStatus(userId, tenantId) {
  if (!userId || !tenantId) return null;
  try {
    const res = await fetch(`${API_BASE_URL}/api/settings/setup-status`, {
      credentials: 'include',
      headers: getUserSettingsHeaders(userId, tenantId),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.success ? data : null;
  } catch {
    /* A checklist that cannot be read must not block the tour, and must not
     * claim everything is fine either — the caller treats null as "unknown"
     * and shows nothing. */
    return null;
  }
}

/** Start the cooldown. Call only once the item has actually been SHOWN. */
export async function recordSetupShown(userId, tenantId, items) {
  return postSetupSignal('shown', userId, tenantId, items);
}

/**
 * The rep said they will not do it. PERMANENT — never call this for a
 * "maybe later"; that is `recordSetupShown`, which comes back in a week.
 */
export async function recordSetupDeclined(userId, tenantId, items) {
  return postSetupSignal('decline', userId, tenantId, items);
}

async function postSetupSignal(kind, userId, tenantId, items) {
  const list = (Array.isArray(items) ? items : [items]).filter(Boolean);
  if (!userId || !tenantId || list.length === 0) return false;
  try {
    const res = await fetch(`${API_BASE_URL}/api/settings/setup-status/${kind}`, {
      method: 'POST',
      credentials: 'include',
      headers: getUserSettingsHeaders(userId, tenantId),
      body: JSON.stringify({ items: list }),
    });
    return res.ok;
  } catch {
    return false;
  }
}
